import React, { useState, useEffect, useRef } from 'react';
import { ImageList as MuiImageList, List, Menu, MenuItem, Icon } from '@mui/material';
import { OpenMenu } from './ReportPhotos.styles';
import useRetrieveData from 'global/hooks/useRetrieveData';
import withInfiniteScroll from 'global/components/withInfiniteScroll';
import { get, set } from 'global/lib/lodash';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { buildFilters } from 'global/utils/filter';
import { PhotoRow } from './PhotoRow';
import useImageColumns from 'global/hooks/useImageColumns';
import { stopProp } from 'global/utils';
import withSearchBar from 'global/components/withSearchBar';
import { Header } from '../ReportList';
import setFilter from 'redux/actions/setFilter';
import { dedupePhotosByFilenameAndHash, filterNewPhotosAgainstExisting } from './dedupePhotos';

const PhotoListWrapper = ({ children, selectedOption, inModal, maxColumns }) => {
  const cols = useImageColumns({ inModal, maxColumns });

  if (selectedOption?.id === 'highlights') {
    return <List>{children}</List>;
  }

  return (
    <MuiImageList
      cols={cols}
      gap={8}
      sx={{
        margin: 0,
        width: '100%',
        '& .MuiImageListItem-root': { aspectRatio: '1' },
      }}
    >
      {children}
    </MuiImageList>
  );
};

export default function ReportPhotos(props) {
  const filter = useSelector((state) => get(state, 'filter.reports.photo'), shallowEqual);
  const realm = useSelector((state) => get(state, 'auth.realm.id'));
  const dispatch = useDispatch();

  const [state, setState] = useState({
    photos: [],
    allPhotos: [],
    opts: [
      { title: 'All Photos', id: 'all' },
      { title: 'Highlights', id: 'highlights' },
      { title: 'Scores', id: 'scores' },
      { title: 'Tasks', id: 'tasks' },
    ],
    selectedOpt: 0,
    maxPageSize: 1000,
    pageSize: 28,
    anchor: null,
    selected: {},
  });

  const selectedOption = get(state, `opts.${state.selectedOpt}`, {});
  // Set when search/filters reset to page 1 so we never append onto a stale page.
  const listResetRef = useRef(false);
  // After a reset, ignore leftover hook `data` until the new request has started.
  const sawLoadingSinceResetRef = useRef(true);
  // Raw search hits keyed by response page so pagination cannot double-append
  // a stale `data` payload when requestPage advances before the next response.
  const photosByPageRef = useRef({});
  // Displayed (post-dedupe) photos — append-only across pages until search resets.
  const displayedPhotosRef = useRef([]);
  // Pages whose results have already been merged into the displayed list.
  const appliedPagesRef = useRef(new Set());

  const includeRealm = get(filter, '_includeRealm', true) !== false;

  const filterProps = () =>
    buildFilters(filter, {
      includeRealm,
      convertName: {
        realm_type: 'tier',
        realm: 'realm_id',
        tier: 'realm_tier',
      },
      dontSort: true,
      dontFilter: ['ownership', 'searchstring', 'edition_id', 'status', 'realms'],
      overrideFilter: (_filt) => {
        const filt = { ..._filt };
        const { id } = selectedOption;

        Object.keys(filt).map((k) => {
          // template: 0 ("Not Template") must be kept for highlights search
          if ((k !== 'quality_type' && k !== 'template' && filt[k] === 0) || (k === 'quality_type' && (filt[k] === 1 || id === 'highlights'))) {
            //highquality return all images but then filter the images returned
            delete filt[k];
          }
        });

        if (id !== 'all' && id !== 'highlights') {
          const collName = id === 'scores' ? 'scorecard-score-images' : 'task-images';
          set(filt, 'collection_name', collName);
        }

        // Highlight-only filters — only send on /search/report-sections
        if (id !== 'highlights') {
          delete filt.section_type;
          delete filt.template;
        }

        return filt;
      },
    });

  const [urlParams, setUrlParams] = useState({
    waitFor: null,
    useNamespace: false,
    updateV: 0,
    transformData: (d) => get(d, 'data', []).map((dd) => ({ ...dd, collectionName: get(dd, 'collectionName', get(dd, 'collection_name')) })),
    params: {
      page: { size: state.pageSize, number: 1 },
      ...filterProps(),
    },
  });
  const { data, meta, loading, error, clearData, update } = useRetrieveData(
    {
      url: get(selectedOption, 'id') === 'highlights' ? '/search/report-sections' : '/search/media',
    },
    urlParams
  );

  const changeOpt = (index) => {
    const opt = get(state, `opts.${index}`);

    if (opt.id === selectedOption.id) {
      return setState((s) => ({ ...s, anchor: null }));
    }

    listResetRef.current = true;
    sawLoadingSinceResetRef.current = false;
    photosByPageRef.current = {};
    displayedPhotosRef.current = [];
    appliedPagesRef.current = new Set();
    clearData();
    set(urlParams, 'params.filter.collection_name', undefined);

    const nextFilter = { ...filter, _photoSource: opt.id };
    if (opt.id !== 'highlights') {
      nextFilter.section_type = null;
      nextFilter.template = null;
    }
    dispatch(setFilter('reports', 'photo', nextFilter));

    setState((s) => ({
      ...s,
      pageSize: s.pageSize,
      selectedOpt: index,
      photos: [],
      allPhotos: [],
      isLoading: true,
      anchor: null,
    }));
  };

  useEffect(() => {
    // Keep sidebar highlight-only filters in sync with the selected photo source
    if (get(filter, '_photoSource') !== selectedOption.id) {
      const next = { ...filter, _photoSource: selectedOption.id };
      if (selectedOption.id !== 'highlights') {
        next.section_type = null;
        next.template = null;
      }
      dispatch(setFilter('reports', 'photo', next));
    }
  }, [selectedOption.id]);

  useEffect(() => {
    // Reset list immediately on search/filter/realm/source changes so prior
    // pages (and their first tile) cannot linger while the new request loads.
    listResetRef.current = true;
    sawLoadingSinceResetRef.current = false;
    photosByPageRef.current = {};
    displayedPhotosRef.current = [];
    appliedPagesRef.current = new Set();
    setState((s) => ({ ...s, photos: [], allPhotos: [], isLoading: true }));
    setUrlParams((p) => ({
      ...p,
      updateV: p.updateV + 1,
      waitFor: undefined,
      useEffect: true,
      returnRaw: true,
      params: {
        ...p.params,
        ...{ page: { size: state.pageSize, number: 1 } },
        q: filter.searchstring,
        ...filterProps(),
      },
    }));
  }, [filter, includeRealm ? realm : undefined, selectedOption]);

  const filterImages = (images = []) => {
    const { id } = selectedOption;

    const quality_type = get(filter, 'quality_type');
    if (id === 'highlights') {
      if (quality_type === 2) {
        //highquality
        return images.map((img) => {
          const { section_type } = img;
          if (section_type === 'Quality Issues') {
            img.images = img.images.filter((im) => im.collection_name === 'report-section-bp-images');
          }
          return img;
        });
      } else if (quality_type === 0) {
        //poor qual
        return images.map((img) => {
          const { section_type } = img;
          if (section_type === 'Quality Issues') {
            img.images = img.images.filter((im) => im.collection_name === 'report-section-site-images');
          }
          return img;
        });
      }
    }

    return images;
  };

  const decoratePhoto = (img, i) => ({
    ...img,
    parentId: get(img, 'task.id'),
    canSelect: !!props.canSelect,
    index: i,
    onClose: (s) => {
      if (s.hasEdits) {
        clearData();
        update();
      }
    },
    selectImage,
  });

  const requestPage = get(urlParams, 'params.page.number', 1);
  const responsePage = get(meta, 'page.current-page');
  const lastPage = get(meta, 'page.last-page', null);
  const updatePage = (newPage) => {
    if (newPage <= lastPage) {
      setUrlParams({
        ...urlParams,
        params: {
          ...urlParams.params,
          page: { size: state.pageSize, number: newPage },
        },
      });
    }
  };

  useEffect(() => {
    if (loading) {
      sawLoadingSinceResetRef.current = true;
      setState((s) => ({ ...s, isLoading: true }));
      return;
    }

    // Filter/search reset clears the UI immediately, but hook `data` can still be
    // the previous response until the new request starts. Skip that stale payload.
    if (listResetRef.current && !sawLoadingSinceResetRef.current) {
      return;
    }

    // requestPage flips as soon as we ask for the next page, while `data`/`meta`
    // still belong to the previous response. Wait until they match.
    if (responsePage != null && Number(responsePage) !== Number(requestPage)) {
      return;
    }

    let cancelled = false;
    const pageKey = Number(responsePage != null ? responsePage : requestPage) || 1;
    const isReset = listResetRef.current || pageKey <= 1;

    if (isReset) {
      listResetRef.current = false;
      photosByPageRef.current = { [pageKey]: data || [] };
      displayedPhotosRef.current = [];
      appliedPagesRef.current = new Set();
    } else if (appliedPagesRef.current.has(pageKey)) {
      setState((s) => ({ ...s, isLoading: false }));
      return;
    } else {
      photosByPageRef.current = {
        ...photosByPageRef.current,
        [pageKey]: data || [],
      };
    }

    const pagePhotos = filterImages(data || []);
    const allPhotos = Object.keys(photosByPageRef.current)
      .map(Number)
      .sort((a, b) => a - b)
      .flatMap((page) => photosByPageRef.current[page] || []);

    (async () => {
      const isHighlights = get(selectedOption, 'id') === 'highlights';
      let nextDisplayed;

      if (isReset) {
        // First page / new search: build the initial stable list.
        const initial = isHighlights ? pagePhotos : await dedupePhotosByFilenameAndHash(pagePhotos);
        if (cancelled) return;
        nextDisplayed = initial.map((img, i) => decoratePhoto(img, i));
      } else {
        // Later pages: only append non-duplicates. Never rewrite already-shown tiles.
        const existing = displayedPhotosRef.current;
        const additions = isHighlights
          ? pagePhotos
          : await filterNewPhotosAgainstExisting(pagePhotos, existing);
        if (cancelled) return;
        const startIndex = existing.length;
        nextDisplayed = [...existing, ...additions.map((img, i) => decoratePhoto(img, startIndex + i))];
      }

      displayedPhotosRef.current = nextDisplayed;
      appliedPagesRef.current.add(pageKey);
      setState((s) => ({ ...s, photos: nextDisplayed, allPhotos, isLoading: false }));
    })();

    return () => {
      cancelled = true;
    };
  }, [data, selectedOption, loading, requestPage, responsePage]);

  const closeContainer = () => {
    setState((s) => ({ ...s, anchor: null }));
  };

  const clickDrop = (e) => {
    stopProp(e);
    setState((s) => ({ ...s, anchor: e.currentTarget }));
  };

  const selectImage = (img) => {
    setState((s) => {
      const selected = { ...s.selected };
      const isSel = get(selected, img.id);
      set(selected, img.id, !!isSel ? undefined : img);

      props.onSelectedChange && props.onSelectedChange(selected);

      return {
        ...s,
        selected,
      };
    });
  };

  return (
    <div onClick={closeContainer} data-testid="assessment-all-photos">
      <Header {...props} title={"All Photos"}/>

      <ListWithPages
        handlePageChange={updatePage}
        currentPage={get(urlParams, 'params.page.number', 1)}
        lastPage={lastPage}
        data={state.photos}
        loading={loading || state.isLoading}
        error={error}
        selectedOption={selectedOption}
        listProps={{ selectedOption, inModal: props.inModal, maxColumns: props.maxColumns }}
        alwaysReplaceData
        loadWhileIntersecting
        selected={state.selected}
        header={
          <>
            <OpenMenu onClick={clickDrop}>
              {get(state, `opts.${state.selectedOpt}.title`)}
              <div className="more">
                {`more`} <Icon>arrow_drop_down</Icon>
              </div>
            </OpenMenu>
            <Menu anchorEl={state.anchor} open={!!state.anchor}>
              {state.opts.map((opt, i) => {
                return (
                  <MenuItem key={i} selected={state.selectedOpt === i} onClick={() => changeOpt(i)}>
                    {opt.title}
                  </MenuItem>
                );
              })}
            </Menu>
          </>
        }
        {...props}
      />
    </div>
  );
}

const ListWithPages = withSearchBar(withInfiniteScroll(PhotoRow, PhotoListWrapper));
